import type { IconName } from '@kiips/ui';

/**
 * UI Layer registry.
 *
 * A "layer" is a semantic category of secondary information that may render
 * across multiple surfaces — card overlays, day-bar overlays, Review
 * sub-tabs, sections inside DetailsView. Toggling a layer affects ALL its
 * surfaces consistently.
 *
 * Adding a new layer:
 *   1. Add an entry to LAYER_IDS below.
 *   2. Add an entry to LAYER_REGISTRY with metadata + default visibility.
 *   3. Wrap each rendering surface in `useLayer(layerId)`.
 *
 * Removing a layer:
 *   1. Drop the surfaces (or stop rendering them).
 *   2. Remove from registry. Existing persisted prefs for the dropped id
 *      are harmless — they'll just be ignored.
 *
 * See ADR-0040.
 */

export const LAYER_IDS = [
  'notes',
  'weather',
  'tags',
  'openingHours',
  'photos',
  'transport',
  'references',
  'cost',
] as const;

export type LayerId = (typeof LAYER_IDS)[number];

export interface LayerMetadata {
  id: LayerId;
  label: string;
  /** Icon used in the floating layers menu. */
  icon: IconName;
  /** Short, single-line description for the layers menu (≤ ~40 chars). */
  description: string;
  /** Initial visibility for new users. Preserves current behavior — every
   *  layer that renders today is on by default. */
  defaultOn: boolean;
  /** Layer is gated behind a paid subscription tier. Free users see
   *  the row with a Pro badge; tapping opens the upgrade sheet
   *  instead of toggling. Server-side, the data behind these layers
   *  is also gated via `feature-policy.ts` (ADR-0052) — leaving the
   *  toggle visible-but-locked is what gives Free users visibility
   *  into what they'd unlock by upgrading. Per `docs/TIERS.md`. */
  proOnly?: boolean | undefined;
}

export const LAYER_REGISTRY: Record<LayerId, LayerMetadata> = {
  notes: {
    id: 'notes',
    label: 'Notes',
    icon: 'sticky-note-2',
    description: 'Note icons + Review > Notes',
    defaultOn: true,
  },
  weather: {
    id: 'weather',
    label: 'Weather',
    icon: 'wb-cloudy',
    description: 'Day pill + per-place forecast',
    defaultOn: true,
    proOnly: true,
  },
  tags: {
    id: 'tags',
    label: 'Tags',
    icon: 'label',
    description: 'Tag chips on cards',
    defaultOn: true,
  },
  openingHours: {
    id: 'openingHours',
    label: 'Opening hours',
    icon: 'schedule',
    description: 'Greys out closed places',
    defaultOn: true,
    proOnly: true,
  },
  photos: {
    id: 'photos',
    label: 'Photos',
    icon: 'photo',
    description: 'Place photos on cards',
    defaultOn: true,
  },
  transport: {
    id: 'transport',
    label: 'Transport',
    icon: 'directions',
    description: 'Connector chips between places',
    defaultOn: true,
  },
  references: {
    id: 'references',
    label: 'References',
    icon: 'menu-book',
    description: 'Country info, maps, visa, currency',
    defaultOn: true,
  },
  cost: {
    id: 'cost',
    label: 'Cost rollups',
    icon: 'payments',
    // Reporting surface only — the per-currency day/trip spend rollups
    // (CostChip `totals`). Entering costs and settling up with
    // companions stay on Free; this Pro layer is the aggregated
    // reporting view. See ADR-0059 (tier gating).
    description: 'Day & trip spend rollups',
    defaultOn: true,
    proOnly: true,
  },
};

/**
 * Pure resolver. With density modes gone, this just looks up the user's
 * stored preference; if no entry exists for a layer (e.g. brand-new layer
 * added in a future version), it falls back to the registry default.
 */
export function resolveLayer(layerId: LayerId, prefs: { [K in LayerId]?: boolean | undefined }): boolean {
  const v = prefs[layerId];
  if (v !== undefined) return v;
  return LAYER_REGISTRY[layerId].defaultOn;
}

/** All layer ids in registry order — convenient for UI lists. */
export function listLayers(): LayerMetadata[] {
  return LAYER_IDS.map((id) => LAYER_REGISTRY[id]);
}

/** Build a fresh prefs map with every layer set to its registry default. */
export function defaultLayerPrefs(): Record<LayerId, boolean> {
  const out = {} as Record<LayerId, boolean>;
  for (const id of LAYER_IDS) out[id] = LAYER_REGISTRY[id].defaultOn;
  return out;
}
